import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ReportConfigPanel from './components/ReportConfigPanel';
import ReportPreview from './components/ReportPreview';
import AuditPackExport from './components/AuditPackExport';
import DateRangeBar from './components/DateRangeBar';
import ReportHistory from './components/ReportHistory';
import Select from '../../components/ui/Select';
import { siteService } from '../../services/siteService';
import { auditService } from '../../services/auditService';
import { checklistService } from '../../services/checklistService';


import { useAuth } from '../../contexts/AuthContext';
import { reportService } from '../../services/reportService';
import { pdfExportService } from '../../services/pdfExportService';
import { getDefaultDashboard } from '../../utils/roleAccess';
import { useLanguage } from '../../contexts/LanguageContext';

const ReportBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const previewRef = useRef(null);

  // Config state
  const [selectedSite, setSelectedSite] = useState(searchParams?.get('site') || 'all');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d?.setMonth(d?.getMonth() - 1);
    return d?.toISOString()?.split('T')?.[0];
  });
  const [dateTo, setDateTo] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [selectedModules, setSelectedModules] = useState(['tasks', 'documents', 'rams', 'incidents', 'snags', 'certificates', 'photos', 'audits', 'checklists']);
  const [reportType, setReportType] = useState('compliance');
  const [outputFormat, setOutputFormat] = useState('pdf');

  // Data state
  const [sites, setSites] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExportingAuditPack, setIsExportingAuditPack] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState('builder'); // 'builder' | 'audit_pack'

  // Derived: To-before-From validation. YYYY-MM-DD string comparison is safe.
  // Empty inputs are allowed (open-ended ranges fall through to "all time"
  // on that side per reportService.filterByDate's existing behaviour).
  const dateRangeError = (dateFrom && dateTo && dateFrom > dateTo)
    ? 'To date must be on or after From date.'
    : '';

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      // Role-based site scope.
      // Admin: RLS scopes getAllSites() to their company.
      // Site Manager: assigned sites (worker_site_assignments + site_manager_id),
      //   falling back to company-scoped sites when no direct assignment exists.
      // Client: company sites filtered to client_visible.
      const result = userProfile?.role === 'site_manager'
        ? await siteService?.getSitesByUser()
        : await siteService?.getAllSites();
      let allSites = result?.data || [];

      if (userProfile?.role === 'client') {
        allSites = allSites?.filter((s) => s?.client_visible);
      }

      setSites(allSites);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const handleModuleToggle = (moduleId) => {
    setSelectedModules(prev =>
      prev?.includes(moduleId)
        ? prev?.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSelectAllModules = () => {
    const ALL_MODULES = ['snags', 'incidents', 'documents', 'rams', 'diary', 'photos', 'certificates', 'audits'];
    if (selectedModules?.length === ALL_MODULES?.length) {
      setSelectedModules([]);
    } else {
      setSelectedModules(ALL_MODULES);
    }
  };

  const selectedSiteName = selectedSite !== 'all'
    ? sites?.find(s => s?.id === selectedSite)?.site_name || sites?.find(s => s?.id === selectedSite)?.name
    : null;

  const handleGenerateReport = async () => {
    if (dateRangeError) {
      setError(dateRangeError);
      return;
    }
    try {
      setIsGenerating(true);
      setError('');
      setReportData(null);

      const siteId = selectedSite !== 'all' ? selectedSite : null;

      const { data, error: fetchError } = await reportService?.fetchReportData({
        siteId,
        companyId: userProfile?.company_id,
        dateFrom,
        dateTo,
        modules: selectedModules,
      });

      if (fetchError) throw fetchError;

      const allDocs = data?.documents || [];
      const certificates = allDocs?.filter(d =>
        d?.category?.toLowerCase()?.includes('cert') ||
        d?.category?.toLowerCase()?.includes('qualification')
      );

      let auditData = [];
      if (selectedModules?.includes('audits')) {
        const auditResult = selectedSite !== 'all'
          ? await auditService?.getAuditsBySite(selectedSite)
          : await auditService?.getAllAudits();
        if (auditResult?.error) {
          console.error('[ReportBuilder] audit data failed:', { code: auditResult?.error?.code, message: auditResult?.error?.message });
        }
        auditData = auditResult?.data || [];
      }

      let checklistData = [];
      if (selectedModules?.includes('checklists')) {
        const companyId = userProfile?.company_id;
        const siteId = selectedSite !== 'all' ? selectedSite : null;
        const checklistResult = await checklistService?.getCompletionsForReport(siteId, companyId, dateFrom, dateTo);
        checklistData = checklistResult?.data || [];
      }

      setReportData({
        site: data?.site || null,
        documents: data?.documents || [],
        incidents: data?.incidents || [],
        snags: data?.snags || [],
        tasks: data?.tasks || [],
        certificates: data?.certificates || certificates,
        photos: data?.photos || [],
        audits: auditData,
        checklists: checklistData,
      });

    } catch (err) {
      setError('Failed to generate report. Please try again.');
      console.error('Report generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportData) return;
    try {
      setIsDownloading(true);
      setError('');
      const { filename, error: pdfError } = await pdfExportService?.generateComplianceReport({
        reportData,
        siteName: selectedSiteName,
        dateFrom,
        dateTo,
        reportType,
        selectedModules,
      });
      if (pdfError) throw pdfError;
      setSuccessMsg(`PDF exported: ${filename}`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      setError('Failed to generate PDF. Please try again.');
      console.error('PDF export error:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!reportData) return;
    try {
      const rows = [];
      const headers = ['Type', 'Title/Description', 'Status', 'Date', 'Site', 'Category'];
      rows?.push(headers);

      reportData?.documents?.forEach(doc => {
        rows?.push(['Document', doc?.title || '', doc?.status || '', doc?.created_at?.split('T')?.[0] || '', doc?.site_name || '', doc?.category || '']);
      });
      reportData?.incidents?.forEach(inc => {
        rows?.push(['Incident', inc?.title || inc?.description || '', inc?.severity || '', inc?.incident_date || '', inc?.site_name || '', inc?.incident_type || '']);
      });

      const csv = rows?.map(r => r?.map(c => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `siteproof-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
      setSuccessMsg('CSV downloaded successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Failed to export CSV.');
    }
  };

  // Map AuditPackExport toggle IDs → reportService module names. With the
  // wqugEUH0 PR 2, diary/near_misses/rams/photos are all wired end-to-end
  // (fetcher in reportService + renderer in pdfExportService.generateComplianceReport).
  // The three non-functional toggles (site_overview, audit_trail, signatures)
  // were removed from the UI in the same PR.
  const AUDIT_PACK_SECTION_TO_MODULE = {
    documents: 'documents',
    incidents: 'incidents',
    near_misses: 'near_misses',
    snags: 'snags',
    audits: 'audits',
    diary: 'diary',
    rams: 'rams',
    photos: 'photos',
  };

  const handleAuditPackExport = async (includedSections) => {
    if (dateRangeError) {
      setError(dateRangeError);
      return;
    }
    try {
      setIsExportingAuditPack(true);
      setError('');

      const modules = (includedSections || [])
        ?.map(s => AUDIT_PACK_SECTION_TO_MODULE?.[s])
        ?.filter(Boolean);

      if (modules?.length === 0) {
        setError('Select at least one supported section.');
        return;
      }

      const siteId = selectedSite !== 'all' ? selectedSite : null;

      const { data, error: fetchError } = await reportService?.fetchReportData({
        siteId,
        companyId: userProfile?.company_id,
        dateFrom,
        dateTo,
        modules,
      });
      if (fetchError) throw fetchError;

      // Audits live in a separate service (mirrors handleGenerateReport).
      let auditData = [];
      if (modules?.includes('audits')) {
        const auditResult = selectedSite !== 'all'
          ? await auditService?.getAuditsBySite(selectedSite)
          : await auditService?.getAllAudits();
        if (auditResult?.error) {
          console.warn('[ReportBuilder] audit data failed (non-fatal for pack):', { code: auditResult?.error?.code, message: auditResult?.error?.message });
        }
        auditData = auditResult?.data || [];
      }

      const packData = {
        site: data?.site || null,
        documents: data?.documents || [],
        incidents: data?.incidents || [],
        snags: data?.snags || [],
        tasks: data?.tasks || [],
        certificates: data?.certificates || [],
        photos: data?.photos || [],
        diary: data?.diary || [],
        near_misses: data?.near_misses || [],
        rams: data?.rams || [],
        audits: auditData,
        checklists: [],
      };

      const { filename, error: pdfError } = await pdfExportService?.generateComplianceReport({
        reportData: packData,
        siteName: selectedSiteName,
        dateFrom,
        dateTo,
        reportType: 'audit_pack',
        selectedModules: modules,
      });
      if (pdfError) throw pdfError;

      setSuccessMsg(`Audit Pack exported: ${filename}`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      console.error('[ReportBuilder] Audit pack export error:', err);
      setError('Failed to generate Audit Pack. Please try again.');
    } finally {
      setIsExportingAuditPack(false);
    }
  };

  const breadcrumbItems = [
    { label: t('dashboard'), href: getDefaultDashboard(userProfile?.role) },
    { label: t('reportBuilder') },
  ];

  return (
    <>
      <Helmet>
        <title>Report Builder – SiteProof</title>
      </Helmet>
      <Header />
      <main className="min-h-screen bg-background pt-16">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground font-heading">{t('reportBuilder')}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('reportBuilderSubtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {reportData && (
                <>
                  {outputFormat === 'csv' ? (
                    <Button variant="outline" onClick={handleDownloadCSV} className="flex items-center gap-2">
                      <Icon name="Download" size={16} />
                      {t('downloadCSV')}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleDownloadPDF}
                      disabled={isDownloading}
                      className="flex items-center gap-2"
                    >
                      {isDownloading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                      ) : (
                        <Icon name="Download" size={16} />
                      )}
                      {t('downloadPDF')}
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="primary"
                onClick={handleGenerateReport}
                disabled={isGenerating || !!dateRangeError}
                className="flex items-center gap-2"
              >
                {isGenerating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Icon name="FileBarChart" size={16} />
                )}
                {isGenerating ? t('generating') : t('generateReport')}
              </Button>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
              <Icon name="AlertCircle" size={16} className="text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
              <button onClick={() => setError('')} className="ml-auto">
                <Icon name="X" size={14} className="text-red-500" />
              </button>
            </div>
          )}
          {successMsg && (
            <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-4">
              <Icon name="CheckCircle" size={16} className="text-emerald-600 flex-shrink-0" />
              <p className="text-sm text-emerald-700">{successMsg}</p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-1 p-1 bg-muted rounded-xl mb-6 w-fit">
            <button
              onClick={() => setActiveTab('builder')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'builder' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="FileBarChart" size={15} />
              {t('reportBuilderTab')}
            </button>
            <button
              onClick={() => setActiveTab('audit_pack')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'audit_pack' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Package" size={15} />
              {t('auditPackTab')}
            </button>
          </div>

          {/* Shared filter bar — site + date range apply to both tabs */}
          <div className="bg-card border border-border rounded-xl p-4 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site / Project</label>
                <Select
                  options={[
                    { value: 'all', label: 'All Sites' },
                    ...sites?.map(s => ({ value: s?.id, label: s?.site_name || s?.name })),
                  ]}
                  value={selectedSite}
                  onChange={(val) => setSelectedSite(val)}
                  placeholder="Select site..."
                />
              </div>
              <DateRangeBar
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
                error={dateRangeError}
              />
            </div>
          </div>

          {activeTab === 'builder' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Config Panel */}
              <div className="lg:col-span-1 space-y-4">
                <ReportConfigPanel
                  selectedModules={selectedModules}
                  onModuleToggle={handleModuleToggle}
                  onSelectAllModules={handleSelectAllModules}
                  reportType={reportType}
                  onReportTypeChange={setReportType}
                  outputFormat={outputFormat}
                  onOutputFormatChange={setOutputFormat}
                />
                <ReportHistory />
              </div>

              {/* Right: Preview */}
              <div className="lg:col-span-2" ref={previewRef}>
                {/* Quick stats bar */}
                {reportData && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[
                      { label: 'Documents', value: reportData?.documents?.length, icon: 'FileText', color: 'bg-blue-50 text-blue-700' },
                      { label: 'Incidents', value: reportData?.incidents?.length, icon: 'ShieldAlert', color: 'bg-red-50 text-red-700' },
                      { label: 'Snags', value: reportData?.snags?.length, icon: 'AlertTriangle', color: 'bg-amber-50 text-amber-700' },
                      { label: 'Certificates', value: reportData?.certificates?.length, icon: 'Award', color: 'bg-emerald-50 text-emerald-700' },
                    ]?.map(stat => (
                      <div key={stat?.label} className={`rounded-xl p-3 ${stat?.color} border border-current/10`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon name={stat?.icon} size={13} />
                          <span className="text-xs font-medium">{stat?.label}</span>
                        </div>
                        <p className="text-2xl font-bold font-heading">{stat?.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                <ReportPreview
                  reportData={reportData}
                  selectedModules={selectedModules}
                  siteName={selectedSiteName}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  reportType={reportType}
                  isLoading={isGenerating}
                />
              </div>
            </div>
          )}

          {activeTab === 'audit_pack' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AuditPackExport
                onExport={handleAuditPackExport}
                isExporting={isExportingAuditPack}
                selectedSite={selectedSite !== 'all' ? selectedSite : null}
                siteName={selectedSiteName}
                disabled={!!dateRangeError}
              />

              {/* Audit Pack Info */}
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-foreground font-heading mb-3">What's in an Audit Pack?</h3>
                  <div className="space-y-3">
                    {[
                      { icon: 'Shield', title: 'Full Compliance Evidence', desc: 'All documents, certificates, and sign-offs in one package' },
                      { icon: 'History', title: 'Audit Trail Summary', desc: 'Timestamped log of every edit, upload, and signature' },
                      { icon: 'FileText', title: 'Regulatory Formatting', desc: 'Formatted for submission to HSE, local authorities, and insurers' },
                      { icon: 'Lock', title: 'Tamper-Evident', desc: 'Digital signatures and checksums verify document integrity' },
                    ]?.map(item => (
                      <div key={item?.title} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={item?.icon} size={15} className="text-accent" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{item?.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item?.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={15} className="text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">UK Construction Compliance</p>
                      <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                        Audit Packs are formatted to meet CDM 2015 requirements, HSE inspection standards, and ISO 9001 audit trail requirements. Includes H&S document records, toolbox talk sign-offs, and CSCS card verification.
                      </p>
                    </div>
                  </div>
                </div>

                <ReportHistory />
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default ReportBuilder;
